import { ReactNode } from 'react'
import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { ChevronRight } from 'lucide-react'

interface GradientBorderCardProps {
  /** Card content */
  children?: ReactNode
  /** Max width of the card (default: '3xl' = 48rem) */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  /** Padding size (default: 'lg') */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  /** Whether content is centered (default: true) */
  centered?: boolean
  /** Additional CSS classes */
  className?: string
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  full: 'max-w-full',
}

const paddingClasses = {
  none: '',
  sm: 'p-4 md:p-6',
  md: 'p-6 md:p-8',
  lg: 'p-8 md:p-12',
  xl: 'p-12 md:p-16',
}

/**
 * A card component with a gradient border (orange → purple → orange)
 * and a solid dark background.
 * 
 * Uses CSS background-clip technique to create proper gradient border:
 * - Solid dark background fills the padding area
 * - Gradient border fills the border area
 */
export function GradientBorderCard({
  children,
  maxWidth = '3xl',
  padding = 'lg',
  centered = true,
  className = '',
}: GradientBorderCardProps) {
  return (
    <div 
      className={`mx-auto ${maxWidthClasses[maxWidth]} rounded-2xl ${paddingClasses[padding]} ${centered ? 'text-center' : ''} ${className}`}
      style={{
        background: 'linear-gradient(#0f0f0f, #0f0f0f) padding-box, linear-gradient(to right, #f97316, #a855f7, #f97316) border-box',
        border: '2px solid transparent',
      }}
    >
      {children}
    </div>
  )
}

// ============================================================================
// Specialized variants for common use cases
// ============================================================================

interface IconCardProps {
  icon: ReactNode
  title: string
  description: string
  iconBgGradient?: 'primary' | 'purple' | 'orange' | 'emerald'
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  className?: string
}

const iconGradientClasses = {
  primary: 'from-primary/10 to-purple-500/10',
  purple: 'from-purple-500/10 to-primary/10',
  orange: 'from-orange-500/10 to-yellow-500/10',
  emerald: 'from-emerald-500/10 to-teal-500/10',
}

/**
 * A card with an icon, title, and description.
 * Useful for feature cards, value proposition cards, etc.
 */
export function IconCard({
  icon,
  title,
  description,
  iconBgGradient = 'purple',
  maxWidth = '3xl',
  className = '',
}: IconCardProps) {
  return (
    <GradientBorderCard maxWidth={maxWidth} className={className}>
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br mb-6 ${iconGradientClasses[iconBgGradient]}">
        {icon}
      </div>
      <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
        {title}
      </h2>
      <p className="text-muted-foreground text-lg">
        {description}
      </p>
    </GradientBorderCard>
  )
}

// ============================================================================
// Button variants
// ============================================================================

interface CardWithButtonsProps {
  title: string
  description: string
  primaryButton?: {
    text: string
    href: string
    icon?: ReactNode
  }
  secondaryButton?: {
    text: string
    href: string
  }
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  className?: string
}

/**
 * A card with title, description, and optional action buttons.
 */
export function CardWithButtons({
  title,
  description,
  primaryButton,
  secondaryButton,
  maxWidth = '3xl',
  className = '',
}: CardWithButtonsProps) {
  return (
    <GradientBorderCard maxWidth={maxWidth} className={className}>
      <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
        {title}
      </h2>
      <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
        {description}
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        {primaryButton && (
          <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
            <Link href={primaryButton.href}>
              {primaryButton.text}
              {primaryButton.icon || <ChevronRight className="ml-2 w-4 h-4" />}
            </Link>
          </Button>
        )}
        {secondaryButton && (
          <Button variant="outline" size="lg" asChild>
            <Link href={secondaryButton.href}>
              {secondaryButton.text}
            </Link>
          </Button>
        )}
      </div>
    </GradientBorderCard>
  )
}

// ============================================================================
// Content card for arbitrary content
// ============================================================================

interface ContentCardProps {
  /** The main heading title */
  title?: string
  /** An optional icon displayed above the title */
  icon?: ReactNode
  /** Icon background gradient variant */
  iconBgGradient?: 'primary' | 'purple' | 'orange' | 'emerald'
  /** Optional subtitle or description below title */
  subtitle?: string
  /** Child content (paragraphs, buttons, etc.) */
  children?: ReactNode
  /** Max width of the card */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | 'full'
  /** Padding size */
  padding?: 'none' | 'sm' | 'md' | 'lg' | 'xl'
  /** Whether text is centered */
  centered?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * A flexible content card with optional icon, title, subtitle, and children.
 * Use this for the most flexible content rendering.
 */
export function ContentCard({
  title,
  icon,
  iconBgGradient = 'purple',
  subtitle,
  children,
  maxWidth = '3xl',
  padding = 'lg',
  centered = true,
  className = '',
}: ContentCardProps) {
  return (
    <GradientBorderCard 
      maxWidth={maxWidth} 
      padding={padding} 
      centered={centered}
      className={className}
    >
      {icon && (
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${iconGradientClasses[iconBgGradient]} mb-6`}>
          {icon}
        </div>
      )}
      {title && (
        <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-6">
          {subtitle}
        </p>
      )}
      {children}
    </GradientBorderCard>
  )
}
